import {
	Component,
	EventEmitter,
	Injectable,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PlatformOptionConst } from '../../../../../core/const';
import { StatusCampaignEnum } from '../../../../../core/enums';
import { BaseOptionModel } from '../../../../../core/models';
import { Parameter } from '../../../../../core/models/util/api-adapter-service-models';
import { AppResponse } from '../../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../../core/services';
import { generateEnumOption } from '../../../../../core/util';
import { TemplateWAModel } from '../../../../models';
import { ClientDto } from '../../models';

@Component({
	selector: 'lib-filter-in-list-of-campaign',
	templateUrl: './filter-in-list-of-campaign.component.html',
	styleUrls: ['./filter-in-list-of-campaign.component.scss'],
})
@Injectable({
	providedIn: 'root',
})
export class FilterInListOfCampaignComponent implements OnInit, OnDestroy {
	@Output()
	public onChangeFilter: EventEmitter<string>;

	@Output()
	public closeDropdown: EventEmitter<void>;

	public form: FormGroup;
	public channelOption: any[] = PlatformOptionConst;
	public filteredChannelOption: any[] = [];
	public businessNameOption: ClientDto[] = [];
	public filteredBusinessNameOption: ClientDto[] = [];
	public statusOption: BaseOptionModel[] = [];
	public filteredStatusOption: BaseOptionModel[] = [];
	public templateOption: TemplateWAModel[] = [];
	public isHaveNextTemplateOption: boolean = true;
	public isLoadingTemplateOption: boolean = false;

	private pageNoTemplateOption: number = 1;
	private subscribers: Subscription[] = [];

	@Input()
	public filterValue: any;

	constructor(private apiAdapterService: ApiAdapterServiceService) {
		this.closeDropdown = new EventEmitter();
		this.onChangeFilter = new EventEmitter();
	}

	ngOnInit(): void {
		this.statusOption = generateEnumOption(StatusCampaignEnum);
		this.filteredStatusOption = [...this.statusOption];
		this.filteredChannelOption = [...this.channelOption];

		this.initForm();
		this.getBusinessNameOptionService();
	}

	private initForm(): void {
		this.form = new FormGroup({
			businessName: new FormControl(this.filterValue?.businessName ?? ''),
			medium: new FormControl(this.filterValue?.medium ?? ''),
			templateName: new FormControl(this.filterValue?.templateName ?? ''),
			status: new FormControl(this.filterValue?.status ?? ''),
			startDate: new FormControl(this.filterValue?.startDate ?? ''),
			endDate: new FormControl(this.filterValue?.endDate ?? ''),
		});

		if (this.filterValue?.templateName)
			this.templateOption = [
				...this.templateOption,
				this.filterValue?.templateName,
			];
	}

	public getBusinessNameOptionService(): void {
		const subs = this.apiAdapterService
			.executeApiRequest(
				[],
				'/admin/client/all-with-child?pageNo=1&rowPerPage=1000',
				'GET'
			)
			.subscribe((res: AppResponse<ClientDto[]>) => {
				this.businessNameOption = res.data;
				this.filteredBusinessNameOption = [...this.businessNameOption];
			});

		this.subscribers.push(subs);
	}

	public getTemplateOptionService(
		filter: string = '',
		isInfinite: boolean = false
	): void {
		this.isLoadingTemplateOption = true;

		const parameterList: Array<Parameter> = [
			this.initParam('pageNo', this.pageNoTemplateOption, 'number'),
			this.initParam('rowPerPage', 20, 'number'),
			this.initParam('platform', this.form.get('medium').value, 'string'),
			this.initParam('filter', filter, 'string'),
			this.initParam('status', 'APPROVED', 'string'),
		];

		const subs = this.apiAdapterService
			.executeApiRequest(parameterList, '/message/template', 'GET')
			.subscribe((resp: AppResponse<TemplateWAModel[]>) => {
				this.templateOption = isInfinite
					? [...this.templateOption, ...resp.data]
					: resp.data;
				this.isHaveNextTemplateOption = resp.haveNext;
				this.isLoadingTemplateOption = false;
			});

		this.subscribers.push(subs);
	}

	private initParam(_name: string, _value: any, _type: string): Parameter {
		return {
			name: _name,
			value: _value,
			in: 'query',
			type: _type,
		};
	}

	public getNextTemplateBatch() {
		if (!this.isHaveNextTemplateOption) return;

		this.pageNoTemplateOption += 1;
		this.getTemplateOptionService('', true);
	}

	public templateSelectedDisplay(
		attribute1: TemplateWAModel,
		attribute2: TemplateWAModel
	): string {
		return attribute1 && attribute2 && attribute1.name == attribute2.name
			? `${attribute1.name}`
			: '';
	}

	public templateSearch(searchValue: string): void {
		this.pageNoTemplateOption = 1;
		this.getTemplateOptionService(searchValue);
	}

	public businessNameSearch(searchValue: string): void {
		const value = searchValue.toLowerCase();
		this.filteredBusinessNameOption = this.businessNameOption.filter(option =>
			option.name.toLowerCase().includes(value)
		);
	}

	public channelSearch(searchValue: string): void {
		const value = searchValue.toLowerCase();
		this.filteredChannelOption = this.channelOption.filter(option =>
			option.label.toLowerCase().includes(value)
		);
	}

	public statusSearch(searchValue: string): void {
		const value = searchValue.toLowerCase();
		this.filteredStatusOption = this.statusOption.filter(option =>
			option.label.toLowerCase().includes(value)
		);
	}

	public clearDate(): void {
		this.form.get('startDate').setValue(null);
		this.form.get('endDate').setValue(null);
	}

	public validateBackDate(): void {
		if (new Date(this.form.get('startDate').value).getTime() > new Date(this.form.get('endDate').value).getTime())
			this.form.get('endDate').setValue('');
	}

	public onReset(): void {
		this.form.reset();
		this.onChangeFilter.emit(this.form.getRawValue());
	}

	public onCancel(): void {
		this.closeDropdown.emit();
	}

	public onApply(): void {
		this.onChangeFilter.emit(this.form.getRawValue());
		this.closeDropdown.emit();
	}

	ngOnDestroy(): void {
		this.subscribers.forEach(each => each.unsubscribe());
	}
}
